import React from 'react';
import { createPortal } from 'react-dom';

/**
 * 공통 모달 컴포넌트
 * @param {boolean} isOpen - 모달 열림 상태
 * @param {Function} onClose - 모달 닫기 핸들러
 * @param {string} size - 모달 크기 ('sm', 'md', 'lg', 'xl')
 * @param {string} title - 모달 제목
 * @param {ReactNode} children - 모달 바디 내용
 * @param {ReactNode} footer - 모달 푸터 내용
 * @param {boolean} closeOnBackdrop - 배경 클릭으로 닫기 허용
 * @param {boolean} showCloseButton - X 버튼 표시 여부
 * @param {string} className - 추가 CSS 클래스
 */
function Modal({
  isOpen = false,
  onClose,
  size = 'md',
  title,
  children,
  footer,
  closeOnBackdrop = true,
  showCloseButton = true,
  className = '',
  icon,
  subtitle
}) {
  // 모달이 열려있지 않으면 렌더링하지 않음
  if (!isOpen) return null;

  // 배경 클릭 핸들러
  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  // ESC 키 핸들러
  React.useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // 스크롤 방지
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // 모달 크기 클래스
  const sizeClasses = {
    sm: 'modal-sm',
    md: 'modal-md', 
    lg: 'modal-lg',
    xl: 'modal-xl'
  };

  const modalContent = (
    <div 
      className="modal-overlay" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div 
        className={`modal-content ${sizeClasses[size]} ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {/* 모달 헤더 */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            <div className="modal-title-section">
              {title && (
                <h3 id="modal-title" className="modal-title">
                  {icon && <span className="modal-icon">{icon}</span>}
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="modal-subtitle">{subtitle}</p>
              )}
            </div>
            
            {showCloseButton && (
              <button
                className="modal-close-button"
                onClick={onClose}
                aria-label="모달 닫기"
                title="닫기"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* 모달 바디 */}
        <div className="modal-body">
          {children}
        </div>

        {/* 모달 푸터 */}
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Portal을 사용해서 body에 직접 렌더링
  return createPortal(modalContent, document.body);
}

export default Modal;